/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react'
import { get } from 'lodash'
import { useParams } from 'react-router-dom'
import { sendErrorReport, displayValue } from 'shared/helpers'
import {
  Button,
  IconSearch,
  IconCheckmark,
  List,
  Notification,
  Modal,
  Selector,
  TextInput
} from 'shared/components'
import { searchType } from 'shared/constants'
import { fetchUserGroups, addUserToGroup } from 'src/LicenseUsers/actions'

const AddUserToUserGroup = ({ closeCb, confirmCb }) => {
  const [selectedGroup, setSelectedGroup] = useState(null)
  const { companyId, userID } = useParams()

  const [isLoading, setLoading] = useState(true)
  const [list, setList] = useState([])
  // table state
  const [listCount, setListCount] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [currentSort, setSort] = useState([
    {
      desc: false,
      id: 'name'
    }
  ])
  // search state
  const [query, setQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [searchBy, setSearchBy] = useState(get(searchType, 'all'))
  // forms state

  const getUserGroups = useCallback(
    (
      tablePage = 0,
      activeQ = undefined,
      searchTypeSelect = undefined,
      sort = currentSort,
      rows = rowsPerPage
    ) => {
      setLoading(true)
      fetchUserGroups(companyId, tablePage, activeQ, searchTypeSelect, sort, rows)
        .then(res => {
          setList(get(res, 'data.results') || [])
          setListCount(get(res, 'data.count'))
          setLoading(false)
        })
        .catch(err => {
          setLoading(false)
          sendErrorReport(err, 'Cannot fetch customer accounts list')
        })
    },
    [companyId]
  )

  useEffect(() => {
    getUserGroups()
  }, [getUserGroups])

  const handleSearchSubmit = e => {
    e.preventDefault()
    setActiveQuery(query)
    setPage(0)
    getUserGroups(0, query, searchBy, currentSort, rowsPerPage)
  }

  const handleSearchClear = () => {
    setQuery('')
    setActiveQuery('')
    setPage(0)
    getUserGroups(0, undefined, undefined, currentSort, rowsPerPage)
  }

  const handlePageChange = newPage => {
    setPage(newPage)
    getUserGroups(newPage, activeQuery, searchBy, currentSort, rowsPerPage)
  }

  const handleSortChange = newSorted => {
    setSort(newSorted)
    getUserGroups(0, activeQuery, searchBy, newSorted, rowsPerPage)
  }

  const handlePageSizeChange = (newPageSize, newPage) => {
    setPage(newPage)
    setRowsPerPage(newPageSize)
    getUserGroups(newPage, activeQuery, searchBy, currentSort, newPageSize)
  }

  const handleSubmit = () => {
    if (!selectedGroup) {
      Notification.error(__('Please select a user to add to the group'))
      return
    }
    setLoading(true)
    addUserToGroup(companyId, userID, { license_user_group_id: selectedGroup })
      .then(() => {
        setLoading(false)
        confirmCb()
        Notification('success', __('User added to the group successfully'))
      })
      .catch(err => {
        const errorMsg = get(err, 'response.data.detail') || __('Failed to add user to the group')
        setLoading(false)
        sendErrorReport(err, 'Cannot add user to group')
        Notification('error', errorMsg)
      })
  }

  return (
    <Modal
      confirmCb={handleSubmit}
      closeCb={closeCb}
      disabled={isLoading}
      size='xl'
      title={__('Add license user to user group')}
    >
      <div className='AllLicenseUsers'>
        <div className='AllLicenseUsers-public'>
          <div className='list-header'>
            <div>
              <div className='search-selector'>
                <Selector
                  handleChange={val => setSearchBy(val)}
                  options={[
                    { label: __('Search by all'), value: searchType.all },
                    { label: __('Search by code'), value: searchType.code },
                    { label: __('Search by name'), value: searchType.name },
                    {
                      label: __('Search by customer account name'),
                      value: searchType.customerAccountNameIcontains
                    },
                    {
                      label: __('Search by customer account code'),
                      value: searchType.customerAccountCodeIcontains
                    }
                  ]}
                  value={searchBy}
                />
              </div>
              <form onSubmit={handleSearchSubmit}>
                <TextInput handleChange={val => setQuery(val)} value={query} />
                {activeQuery && (
                  <button type='button' onClick={handleSearchClear} disabled={isLoading}>
                    &times;
                  </button>
                )}
                <Button type='submit' theme='info' disabled={isLoading}>
                  <IconSearch fill='#fff' />
                </Button>
              </form>
            </div>
          </div>
          <List
            data={list}
            defaultSorted={currentSort}
            loading={isLoading}
            manual
            minRows={Math.ceil(get(list, 'length')) || 10}
            page={page}
            pages={Math.ceil(listCount / rowsPerPage)}
            onPageChange={handlePageChange}
            onSortedChange={handleSortChange}
            showPagination
            showPageSizeOptions
            pageSize={rowsPerPage}
            onPageSizeChange={(pageSize, pageIndex) => handlePageSizeChange(pageSize, pageIndex)}
            clickable
            handleClick={rowData => {
              const groupID = get(rowData, 'original.id')
              setSelectedGroup(groupID)
            }}
            columns={[
              {
                accessor: 'name',
                Header: __('Name'),
                Cell: cellInfo => displayValue(cellInfo.value)
              },
              {
                accessor: 'code',
                Header: __('Code'),
                Cell: cellInfo => displayValue(cellInfo.value)
              },
              {
                accessor: 'description',
                Header: __('Description'),
                Cell: cellInfo => displayValue(cellInfo.value)
              },
              {
                accessor: 'metadata',
                Header: __('Metadata'),
                Cell: cellInfo => displayValue(JSON.stringify(cellInfo.value))
              },
              {
                show: true,
                Header: __('Selected'),
                id: 'selected',
                accessor: 'null',
                className: 'text-center',
                Cell: cellData => {
                  const groupID = get(cellData, 'original.id')
                  const isSelected = groupID === selectedGroup
                  return (
                    <div className='checkbox-wrapper'>
                      {isSelected && <IconCheckmark color='#10ac84' height='14px' />}
                    </div>
                  )
                },
                width: 85
              }
            ]}
          />
        </div>
      </div>
    </Modal>
  )
}

export default AddUserToUserGroup
